import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useMemo } from "react";
import { useShellRouter } from "@hooks/useShellAwareRouter";
import { buildOpenReplayLabel } from "@utils/openreplay";
import { useChatJsonRenderContext } from "../jsonRender/ChatJsonRenderContext";
import { type ActionButtonsProps, resolveChatActions } from "./chatActions";
import { navigateAfterClosingChat } from "./navigateAfterClosingChat";
import { askEdButtonClass, notificationNormalClass } from "./type";

export type { ActionButtonsProps } from "./chatActions";

const ACTION_BUTTON_OPENREPLAY_LABEL = buildOpenReplayLabel(
  "Chat",
  "ActionButton",
  "Click",
);

export const ActionButtons: React.FC<ActionButtonsProps> = ({ actions }) => {
  const chatContext = useChatJsonRenderContext();
  const router = useShellRouter();
  const streamLoading = !!chatContext?.streamLoading;
  const messageId = chatContext?.messageId ?? "";

  const resolvedActions = useMemo(() => resolveChatActions(actions), [actions]);

  if (resolvedActions.length === 0) {
    return null;
  }

  // The view is reported once per DOM node, so the row remounts when the final message id lands.
  const viewReady = !streamLoading && messageId.length > 0;

  return (
    <div
      key={viewReady ? "view-ready" : "view-pending"}
      className="flex flex-wrap gap-3"
      {...(viewReady
        ? {
            "data-or-view": "chat_cta_view",
            "data-or-param-message_id": messageId,
          }
        : {})}
    >
      {resolvedActions.map((action, index) => {
        const isPrimary = index === 0;
        return (
          <button
            key={`${action.destination}-${index}`}
            type="button"
            className={cn(
              isPrimary ? askEdButtonClass : notificationNormalClass,
              "min-w-[76px] w-fit disabled:cursor-not-allowed disabled:opacity-50",
            )}
            disabled={streamLoading}
            data-openreplay-label={ACTION_BUTTON_OPENREPLAY_LABEL}
            data-or-event="chat_cta_click"
            data-or-param-destination={action.destination}
            data-or-param-action_type={action.actionType}
            data-or-param-position={String(index)}
            data-or-param-message_id={messageId}
            onClick={() => navigateAfterClosingChat(router, action.intent.href)}
          >
            <span
              className={cn(
                "button_s",
                isPrimary
                  ? "text-content_inverse_primary"
                  : "text-content_primary",
              )}
            >
              {action.label}
            </span>
            {isPrimary && (
              <Icon
                name="right-arrow-alt"
                size={16}
                color={ospThemes.colors.content_inverse_primary}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};
